import { useState, useRef, useEffect, useCallback, useMemo, type ChangeEvent, type FormEvent } from 'react';
import { useSignUp, useSignIn } from '@clerk/clerk-react';
import { useQuery } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import type { Id } from '../../../convex/_generated/dataModel';
import { useLanguage } from '../../contexts/useLanguage';
import { saveReservation, ReservationData } from '../../services/reservationService';
import { getClerkErrorMessage } from '../auth/clerkAuthErrors';
import { buildGuestReservationPayload } from './guestReservationFormBuildPayload';
import { CHECKOUT_ZERO_TEST } from '../../lib/checkoutZeroTest';
import { useWebDesignedCart } from '../../contexts/WebDesignedCartContext';
import type { GuestReservationFormProps } from './guestReservationFormTypes';
import { useGuestReservationCountdown } from './useGuestReservationCountdown';
import { useGuestReservationTemporaryLock } from './useGuestReservationTemporaryLock';
import { useGuestReservationOpenBootstrap } from './useGuestReservationOpenBootstrap';
import { useGuestReservationStayDateSync } from './useGuestReservationStayDateSync';
import { createGuestReservationInputHandler } from './guestReservationFormInputHandler';
import { loadConfirmationTemplateFromStorage } from './guestReservationFormConfirmationTemplate';

export function useGuestReservationForm({
  isOpen,
  onClose,
  itemType,
  itemId,
  itemName,
  itemPrice,
  taxeHebergementEnabled: taxeHebergementEnabledProp,
  onReservationSubmit,
  shoppingCartData,
  currentUser,
  onDateChange,
  initialPaymentMode,
}: GuestReservationFormProps) {
  const { t } = useLanguage();
  const cartContext = useWebDesignedCart();
  const vumPromo = cartContext?.vumPromo ?? null;
  const { signUp, isLoaded: signUpLoaded } = useSignUp();
  const { signIn, isLoaded: signInLoaded } = useSignIn();
  const [paymentMode, setPaymentMode] = useState<'guest' | 'member'>(
    initialPaymentMode || (currentUser ? 'member' : 'guest')
  );
  const [isLoggedIn, setIsLoggedIn] = useState(!!currentUser);
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [loginError, setLoginError] = useState('');
  const [loggedInUserId, setLoggedInUserId] = useState<string | null>(currentUser?.id || null);
  const isUpdatingFromExternalRef = useRef(false);
  const isUpdatingFromSelectRef = useRef(false);

  // "Create account" opt-in (guest checkout only)
  const [createAccount, setCreateAccount] = useState(false);
  const [accountPassword, setAccountPassword] = useState('');
  const [accountCreationStatus, setAccountCreationStatus] = useState<'idle' | 'creating' | 'done' | 'error'>('idle');
  const [accountCreationError, setAccountCreationError] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    // Address
    addressLine1: '',
    postalCode: '',
    province: 'QC',
    country: 'CA',
    // Booking
    checkIn: '',
    checkOut: '',
    duration: 1,
    adults: 1,
    children: 0,
    specialRequests: '',
    hearAboutUs: '',
    paymentMethod: 'full',
    couponCode: '',
  });
  const [useDuration, setUseDuration] = useState(true);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [wizardStep, setWizardStep] = useState<1 | 2 | 3>(1);

  useEffect(() => {
    if (isOpen) setWizardStep(1);
  }, [isOpen, itemId]);

  const { timeRemaining, hasStarted: countdownStarted } = useGuestReservationCountdown(
    isOpen,
    itemId,
    t,
    formData.checkIn,
    formData.checkOut,
  );

  const userWithAddress = useQuery(
    api.users.getMeWithAddress,
    currentUser ? { clerkId: currentUser.id } : 'skip'
  );

  const accommodationDoc = useQuery(
    api.accommodations.get,
    itemType === 'accommodation' && itemId ? { id: itemId as Id<'accommodations'> } : 'skip',
  );

  const taxeHebergementEnabled = useMemo(() => {
    if (taxeHebergementEnabledProp !== undefined) {
      return taxeHebergementEnabledProp !== false;
    }
    if (itemType === 'accommodation' && accommodationDoc) {
      return accommodationDoc.taxeHebergementEnabled !== false;
    }
    return true;
  }, [taxeHebergementEnabledProp, itemType, accommodationDoc]);

  const { sessionIdRef, lockIdRef, setLockId, availabilityError, lockedUntil, rateLimitRetryAfterSec } =
    useGuestReservationTemporaryLock({
      isOpen,
      itemType,
      itemId,
      checkIn: formData.checkIn,
      checkOut: formData.checkOut,
    });

  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [pendingReservationData, setPendingReservationData] = useState<any | null>(null);
  const [couponFeedback, setCouponFeedback] = useState<string | null>(null);

  const handleApplyCoupon = useCallback(() => {
    if (formData.couponCode.trim()) {
      setCouponFeedback(t('guestReservation.couponUnknown'));
    } else {
      setCouponFeedback(null);
    }
  }, [formData.couponCode, t]);

  const [submittedReservationId, setSubmittedReservationId] = useState<string | null>(null);
  const [confirmationTemplate, setConfirmationTemplate] = useState<{ subject?: string; content: string } | null>(null);
  const [submittedReservationData, setSubmittedReservationData] = useState<any>(null);
  const [recommendedProducts, setRecommendedProducts] = useState<any[]>([]);
  const [cloverCharge, setCloverCharge] = useState<{ chargeId: string; amount: number; status: string } | null>(null);

  useGuestReservationOpenBootstrap({
    isOpen,
    itemId,
    itemName,
    shoppingCartData,
    currentUser,
    itemType,
    initialPaymentMode,
    t,
    isUpdatingFromExternalRef,
    setFormData,
    setPaymentMode,
    setIsLoggedIn,
    setLoggedInUserId,
    setLoginData,
    setLoginError,
    setRecommendedProducts,
  });

  useGuestReservationStayDateSync({
    formData,
    useDuration,
    onDateChange,
    isUpdatingFromExternalRef,
    isUpdatingFromSelectRef,
    setFormData,
  });

  // When Clerk resolves currentUser after inline sign-in, sync formData (including address if available)
  useEffect(() => {
    if (!isOpen || !currentUser || !isLoggedIn) return;
    const addr = userWithAddress?.address;
    setFormData((prev) => ({
      ...prev,
      firstName: prev.firstName || currentUser.firstName || '',
      lastName: prev.lastName || currentUser.lastName || '',
      email: prev.email || currentUser.email || '',
      phone: prev.phone || currentUser.phone || '',
      addressLine1: prev.addressLine1 || addr?.addressLine1 || '',
      postalCode: prev.postalCode || addr?.postalCode || '',
      province: prev.province || addr?.province || 'QC',
      country: prev.country || addr?.country || 'CA',
    }));
    setLoggedInUserId(currentUser.id);
  }, [currentUser, isLoggedIn, isOpen, userWithAddress]);

  // Prefill guest name + persons from VUM promo code
  useEffect(() => {
    if (!isOpen || !vumPromo) return;
    setFormData((prev) => ({
      ...prev,
      ...(!isLoggedIn && !prev.firstName && vumPromo.guestFirstName ? { firstName: vumPromo.guestFirstName } : {}),
      ...(!isLoggedIn && !prev.lastName && vumPromo.guestLastName ? { lastName: vumPromo.guestLastName } : {}),
      adults: prev.adults > 1 ? prev.adults : Math.min(vumPromo.minPersons ?? 1, vumPromo.maxPersons),
      children: prev.children,
    }));
  }, [isOpen, vumPromo, isLoggedIn]);

  // When VUM covers all nights ($0): force paymentMethod='none', no card needed.
  // When extra nights push price > $0: revert to 'full' and clear stale pending data.
  useEffect(() => {
    if (!isOpen || !vumPromo || itemType !== 'accommodation') return;
    if (!formData.checkIn || !formData.checkOut) return;
    const nights = Math.ceil(
      (new Date(formData.checkOut).getTime() - new Date(formData.checkIn).getTime()) / (1000 * 60 * 60 * 24)
    );
    if (nights > 0 && nights <= vumPromo.nights) {
      // Fully covered — no payment needed
      setFormData((prev) => ({ ...prev, paymentMethod: 'none' }));
      setPendingReservationData(null);
    } else if (nights > vumPromo.nights) {
      // Extra nights — revert to full payment and clear any stale $0 pending data
      setFormData((prev) => ({
        ...prev,
        paymentMethod: prev.paymentMethod === 'none' ? 'full' : prev.paymentMethod,
      }));
      setPendingReservationData(null);
    }
  }, [isOpen, vumPromo, itemType, formData.checkIn, formData.checkOut]);

  const handleInputChange = createGuestReservationInputHandler({
    useDuration,
    onDateChange,
    isUpdatingFromExternalRef,
    setFormData,
  });

  const handleLoginInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setLoginData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setLoginError('');
  };

  const handleLogin = async (e: FormEvent) => {
    e.preventDefault();
    setLoginError('');

    if (!loginData.email || !loginData.password) {
      setLoginError(t('guestReservation.loginErrorRequired'));
      return;
    }

    if (!signInLoaded || !signIn) {
      setLoginError("Service d'authentification non disponible.");
      return;
    }

    try {
      const result = await signIn.create({
        identifier: loginData.email.trim(),
        password: loginData.password,
      });

      if (result.status === 'complete') {
        // Clerk session is now active — AuthContext will auto-update currentUser.
        // Mark as logged in immediately so the form reveals the reservation fields.
        setIsLoggedIn(true);
        setLoggedInUserId(result.createdSessionId ?? null);
        setLoginError('');
      }
    } catch (err) {
      setLoginError(getClerkErrorMessage(err));
    }
  };

  const buildReservationPayload = () =>
    buildGuestReservationPayload(
      itemType,
      itemPrice,
      {
        ...formData,
        // Quick reservation never uses the 30% deposit path.
        paymentMethod: formData.paymentMethod === 'partial' ? 'full' : formData.paymentMethod,
      },
      paymentMode,
      isLoggedIn,
      shoppingCartData?.groups,
      vumPromo,
      taxeHebergementEnabled,
    );

  const finishReservation = (
    totalPrice: number,
    depositAmount: number,
    balanceAmount: number,
    duration: number,
    isGuest: boolean,
    groups: any,
    finalPaymentStatus: 'pending' | 'paid',
    finalStatus: 'pending' | 'confirmed',
    gstAmount?: number,
    qstAmount?: number,
    taxeHebergementAmount?: number,
  ) => {
    const reservationToSave: Omit<ReservationData, 'id' | 'createdAt'> = {
      userId: isGuest ? undefined : loggedInUserId || undefined,
      guestName: isGuest ? `${formData.firstName} ${formData.lastName}` : undefined,
      guestEmail: formData.email,
      guestPhone: formData.phone,
      itemType,
      itemId,
      itemName,
      checkIn: formData.checkIn || new Date().toISOString(),
      checkOut: formData.checkOut || formData.checkIn || new Date().toISOString(),
      guests: Math.max(1, formData.adults + formData.children),
      totalPrice,
      status: finalStatus,
      paymentStatus: finalPaymentStatus,
      paymentMethod: formData.paymentMethod,
      specialRequests: formData.specialRequests,
      hearAboutUs: formData.hearAboutUs || undefined,
      isGuest,
      duration,
      groups,
    };

    const savedReservation = saveReservation(reservationToSave);
    const submittedLockId = lockIdRef.current;
    const submittedSessionId = sessionIdRef.current;
    setLockId(null);
    lockIdRef.current = null;

    // Build address payload (only if guest provided required address fields)
    const hasAddress = formData.postalCode.trim() && formData.province.trim() && formData.country.trim();
    const addressPayload = hasAddress
      ? {
          addressLine1: formData.addressLine1.trim() || undefined,
          postalCode: formData.postalCode.trim(),
          province: formData.province.trim(),
          country: formData.country.trim() || 'CA',
        }
      : undefined;

    onReservationSubmit({
      ...formData,
      itemType,
      itemId,
      itemName,
      itemPrice: totalPrice,
      depositAmount,
      balanceAmount,
      gstAmount,
      qstAmount,
      taxeHebergementAmount,
      status: 'pending',
      createdAt: savedReservation.createdAt,
      reservationId: savedReservation.id,
      isGuest,
      address: addressPayload,
      temporaryReservationId: submittedLockId ?? undefined,
      temporaryReservationSessionId: submittedLockId ? submittedSessionId : undefined,
    });

    const tpl = loadConfirmationTemplateFromStorage();
    if (tpl) {
      setConfirmationTemplate(tpl);
    }

    setIsSubmitted(true);
    setSubmittedReservationId(savedReservation.id);
    setSubmittedReservationData({
      orderNumber: savedReservation.id,
      customerName: isGuest
        ? `${formData.firstName} ${formData.lastName}`
        : currentUser
          ? `${currentUser.firstName} ${currentUser.lastName}`
          : `${formData.firstName} ${formData.lastName}`,
      totalAmount: `$${totalPrice.toFixed(2)} CAD`,
      itemName: itemName,
    });

    // Create Clerk account in background if guest opted in
    if (isGuest && createAccount && accountPassword && signUpLoaded && signUp) {
      setAccountCreationStatus('creating');
      signUp
        .create({
          emailAddress: formData.email,
          password: accountPassword,
          firstName: formData.firstName,
          lastName: formData.lastName,
        })
        .then(() => setAccountCreationStatus('done'))
        .catch((err: unknown) => {
          const msg = (err as any)?.errors?.[0]?.longMessage
            ?? (err as any)?.errors?.[0]?.message
            ?? 'Erreur lors de la création du compte.';
          setAccountCreationError(msg);
          setAccountCreationStatus('error');
        });
    }
  };

  /** Charge is performed inside `CloverPaymentForm`; we finalize the reservation with the returned charge result. */
  const handleCloverPayment = async (chargeResult: { chargeId: string; amount: number; status: string }) => {
    if (!pendingReservationData) return;
    setIsProcessingPayment(true);
    setPaymentError(null);
    try {
      const { totalWithTax, depositAmount, balanceAmount, duration, isGuest, groups, finalPaymentStatus, finalStatus, gstAmount, qstAmount, taxeHebergementAmount } = pendingReservationData;
      setCloverCharge(chargeResult);
      finishReservation(totalWithTax, depositAmount ?? totalWithTax, balanceAmount ?? 0, duration, isGuest, groups, finalPaymentStatus, finalStatus, gstAmount, qstAmount, taxeHebergementAmount);
    } catch (err: unknown) {
      setPaymentError((err as { message?: string })?.message || 'Erreur lors du paiement. Veuillez réessayer.');
    } finally {
      setIsProcessingPayment(false);
    }
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    setPaymentError(null);

    // Step navigation uses explicit Next buttons; submit only on step 3.
    if (wizardStep < 3) return;

    if (!formData.hearAboutUs) {
      setPaymentError('Veuillez indiquer comment vous avez entendu parler de nous.');
      return;
    }

    const payload = buildReservationPayload();

    const method =
      formData.paymentMethod === 'partial' ? 'full' : formData.paymentMethod;
    const needsCard = method === 'full' || method === 'credit';
    if (CHECKOUT_ZERO_TEST && needsCard) {
      finishReservation(
        payload.totalWithTax,
        payload.depositAmount ?? payload.totalWithTax,
        payload.balanceAmount ?? 0,
        payload.duration,
        payload.isGuest,
        payload.groups,
        'paid',
        'confirmed',
        payload.gstAmount,
        payload.qstAmount,
        payload.taxeHebergementAmount,
      );
      return;
    }

    if (needsCard) {
      setPendingReservationData(payload);
      return;
    }

    finishReservation(
      payload.totalWithTax,
      payload.depositAmount ?? payload.totalWithTax,
      payload.balanceAmount ?? 0,
      payload.duration,
      payload.isGuest,
      payload.groups,
      payload.finalPaymentStatus,
      payload.finalStatus,
      payload.gstAmount,
      payload.qstAmount,
      payload.taxeHebergementAmount,
    );
  };

  const isTimeLow = countdownStarted && timeRemaining < 300;

  const resetAfterSuccessClose = () => {
    setIsSubmitted(false);
    setWizardStep(1);
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      addressLine1: '',
      postalCode: '',
      province: 'QC',
      country: 'CA',
      checkIn: '',
      checkOut: '',
      duration: 1,
      adults: 1,
      children: 0,
      specialRequests: '',
      hearAboutUs: '',
      paymentMethod: 'full',
      couponCode: '',
    });
    setIsLoggedIn(false);
    setLoggedInUserId(null);
    setCreateAccount(false);
    setAccountPassword('');
    setAccountCreationStatus('idle');
    setAccountCreationError(null);
    onClose();
  };

  /** Re-attempt Clerk account creation with a new password after a failed attempt (e.g. breached password). */
  const retryAccountCreation = async (newPassword: string) => {
    if (!signUpLoaded || !signUp) return;
    setAccountCreationStatus('creating');
    setAccountCreationError(null);
    try {
      await signUp.create({
        emailAddress: formData.email,
        password: newPassword,
        firstName: formData.firstName,
        lastName: formData.lastName,
      });
      setAccountCreationStatus('done');
    } catch (err: unknown) {
      const msg =
        (err as any)?.errors?.[0]?.longMessage ??
        (err as any)?.errors?.[0]?.message ??
        'Erreur lors de la création du compte.';
      setAccountCreationError(msg);
      setAccountCreationStatus('error');
    }
  };

  return {
    t,
    isOpen,
    onClose,
    itemType,
    itemId,
    itemName,
    itemPrice,
    taxeHebergementEnabled,
    currentUser,
    shoppingCartData,
    onDateChange,
    paymentMode,
    setPaymentMode,
    isLoggedIn,
    setIsLoggedIn,
    loginData,
    handleLoginInputChange,
    loginError,
    timeRemaining,
    formData,
    setFormData,
    wizardStep,
    setWizardStep,
    useDuration,
    setUseDuration,
    isSubmitted,
    submittedReservationId,
    confirmationTemplate,
    submittedReservationData,
    cloverCharge,
    recommendedProducts,
    handleInputChange,
    handleLogin,
    handleSubmit,
    handleCloverPayment,
    pendingReservationData,
    setPendingReservationData,
    paymentError,
    setPaymentError,
    isProcessingPayment,
    availabilityError,
    lockedUntil,
    rateLimitRetryAfterSec,
    isTimeLow,
    countdownStarted,
    isUpdatingFromExternalRef,
    isUpdatingFromSelectRef,
    resetAfterSuccessClose,
    // Account creation
    createAccount,
    setCreateAccount,
    accountPassword,
    setAccountPassword,
    accountCreationStatus,
    accountCreationError,
    retryAccountCreation,
    // VUM promo
    vumPromo,
    couponFeedback,
    handleApplyCoupon,
  };
}
